import React, { useState } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { ImageListItemBar } from '@mui/material';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { useLazyLoading } from '../hooks/useLazyLoading';

/**
 * Lazy loading image component with loading states and error handling
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.srcSet - Image srcSet for responsive images
 * @param {string} props.alt - Alt text for accessibility
 * @param {string} props.title - Image title
 * @param {string} props.downloadUrl - URL for download functionality
 * @param {Object} props.style - Additional styles
 */
const LazyImage = ({
  src,
  srcSet,
  alt,
  title,
  downloadUrl,
  style = {},
  ...props
}) => {
  const { imgRef, isInView, isLoaded, setIsLoaded } = useLazyLoading();
  const [hasError, setHasError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setIsImageLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsImageLoading(false);
  };

  return (
    <Box
      ref={imgRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: 164,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: 1,
        ...style
      }}
      {...props}
    >
      {!isInView && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary'
        }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isInView && !hasError && (
        <>
          {isImageLoading && (
            <Box sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              backgroundColor: 'rgba(245, 245, 245, 0.8)',
              zIndex: 1
            }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <img
            srcSet={srcSet}
            src={src}
            alt={alt}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
            loading="lazy"
          />

          {isLoaded && title && (
            <ImageListItemBar
              title={title}
              sx={{
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
              }}
              actionIcon={
                downloadUrl && (
                  <IconButton
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                    aria-label={`Download ${title}`}
                    component="a"
                    href={downloadUrl}
                    target="_blank"
                    download={true}
                    size="small"
                  >
                    <DownloadForOfflineIcon />
                  </IconButton>
                )
              }
            />
          )}
        </>
      )}

      {hasError && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'error.main',
          flexDirection: 'column',
          gap: 1
        }}>
          <Box sx={{ fontSize: '2rem' }}>📷</Box>
          <Box sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            Failed to load image
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LazyImage;